import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatEur, activityFromPrice } from "@/lib/api";
import { toast } from "sonner";
import { BrandHeader } from "@/components/BrandHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { setWhatsAppActivity } from "@/components/WhatsAppButton";
import { AgeBadges } from "@/lib/ageCategories";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MapPin, Clock, CalendarDays, Users, Check, X, Backpack, Lightbulb, Baby, ExternalLink, ChevronLeft, MessageSquare, Send } from "lucide-react";

function InquiryForm({ activityId }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const cls = "w-full rounded-xl border-2 border-slate-200 focus:border-[#003284] outline-none px-4 py-2.5 transition-colors";
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) { toast.error("Rellena nombre, email y mensaje"); return; }
    setBusy(true);
    try { await api.post(`/activities/${activityId}/inquiry`, f); setSent(true); toast.success("Consulta enviada. Te responderemos pronto."); }
    catch (err) { toast.error(err?.response?.data?.detail || "No se pudo enviar"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm" data-testid="inquiry-form">
      <h3 className="font-black brand-navy flex items-center gap-2 mb-1"><MessageSquare size={18} className="text-[#FF7F27]" /> ¿Tienes dudas? Escríbenos antes de apuntarte</h3>
      <p className="text-sm text-slate-500 mb-4">Cuéntanos tus preguntas, necesidades o cualquier detalle y te contestamos.</p>
      {sent ? (
        <p className="text-emerald-600 font-bold" data-testid="inquiry-sent">¡Gracias! Hemos recibido tu consulta.</p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="inquiry-name" className={cls} placeholder="Tu nombre" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="inquiry-email" type="email" className={cls} placeholder="Tu email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <input data-testid="inquiry-phone" className={cls} placeholder="Móvil con WhatsApp (+34...)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </div>
          <textarea data-testid="inquiry-message" className={cls} rows={3} placeholder="Escribe aquí tu consulta…" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
          <button data-testid="inquiry-submit" disabled={busy} className="bg-[#003284] text-white font-bold rounded-full px-6 py-3 disabled:opacity-50 flex items-center gap-2"><Send size={16} /> {busy ? "Enviando…" : "Enviar mensaje"}</button>
        </form>
      )}
    </div>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
    <h3 className="flex items-center gap-2 text-lg font-black brand-navy mb-3"><Icon size={20} className="text-[#FF7F27]" /> {title}</h3>
    {children}
  </div>
);

export default function ActivityDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/activities/${id}`).then((r) => setA(r.data)).catch(() => navigate("/calendario"));
  }, [id, navigate]);

  useEffect(() => {
    if (a?.title) setWhatsAppActivity(a.title);
    return () => setWhatsAppActivity(null);
  }, [a]);

  if (!a) return <div className="min-h-screen bg-[#F8FAFC]"><BrandHeader /><p className="text-center py-20 text-slate-400">Cargando…</p></div>;

  const canSignup = a.status !== "cancelada" && !a.inscriptions_closed && !a.is_full;
  const dateStr = a.end_date && a.end_date !== a.start_date
    ? `${new Date(a.start_date + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "long" })} – ${new Date(a.end_date + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}`
    : new Date(a.start_date + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-28">
      <BrandHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <button data-testid="back-btn" onClick={() => navigate("/calendario")} className="flex items-center gap-1 text-sm font-bold text-slate-500 hover:brand-navy mb-4"><ChevronLeft size={18} /> Volver al calendario</button>

        <div className="relative rounded-3xl overflow-hidden shadow-lg mb-6">
          <Carousel className="w-full">
            <CarouselContent>
              {(a.photos || []).map((p, i) => (
                <CarouselItem key={i}>
                  <img src={p} alt={`${a.title} ${i + 1}`} className="w-full h-64 sm:h-96 object-cover" />
                </CarouselItem>
              ))}
            </CarouselContent>
            {a.photos?.length > 1 && (<><CarouselPrevious className="left-3" /><CarouselNext className="right-3" /></>)}
          </Carousel>
          <div className="absolute top-4 left-4"><StatusBadge activity={a} size="lg" /></div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black brand-navy leading-tight">{a.title}</h1>
            {a.activity_type && <p className="text-sm font-bold text-[#FF7F27] uppercase tracking-wide mt-1">{a.activity_type}</p>}
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600 mt-3">
              <span className="flex items-center gap-1.5"><CalendarDays size={16} className="text-[#FF7F27]" /> {dateStr}</span>
              <span className="flex items-center gap-1.5"><Clock size={16} className="text-[#FF7F27]" /> {a.schedule}</span>
              <span className="flex items-center gap-1.5"><MapPin size={16} className="text-[#FF7F27]" /> {a.place}</span>
              {a.is_full && <span className="flex items-center gap-1.5"><Users size={16} className="text-[#FF7F27]" /> Completa · lista de espera</span>}
            </div>
            <div className="mt-3"><AgeBadges ids={a.age_categories} showRange /></div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            <Section icon={Lightbulb} title="Descripción">
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">{a.full_description}</p>
            </Section>
            {a.program && <Section icon={CalendarDays} title="Programa"><p className="text-slate-600 leading-relaxed whitespace-pre-line">{a.program}</p></Section>}
            <div className="grid sm:grid-cols-2 gap-4">
              {a.includes && <Section icon={Check} title="Qué incluye"><p className="text-slate-600 whitespace-pre-line">{a.includes}</p></Section>}
              {a.not_includes && <Section icon={X} title="Qué no incluye"><p className="text-slate-600 whitespace-pre-line">{a.not_includes}</p></Section>}
            </div>
            {a.material && <Section icon={Backpack} title="Material necesario"><p className="text-slate-600 whitespace-pre-line">{a.material}</p></Section>}
            {a.recommendations && <Section icon={Lightbulb} title="Recomendaciones"><p className="text-slate-600 whitespace-pre-line">{a.recommendations}</p></Section>}
            {a.faqs?.length > 0 && (
              <Section icon={Lightbulb} title="Preguntas frecuentes">
                <Accordion type="single" collapsible>
                  {a.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`f${i}`}>
                      <AccordionTrigger className="text-left font-bold text-slate-700">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Section>
            )}
          </div>

          <div className="space-y-4">
            <Section icon={Check} title="Precios">
              {a.age_brackets?.length > 0 ? (
                <>
                  <p className="text-xs text-slate-500 mb-2">Precio por edad de cada participante:</p>
                  <ul className="space-y-2" data-testid="age-price-table">
                    {a.age_brackets.map((b) => {
                      const range = b.min_age != null && b.min_age !== "" && (b.max_age != null && b.max_age !== "")
                        ? `${b.min_age}–${b.max_age} años`
                        : (b.max_age != null && b.max_age !== "" ? `hasta ${b.max_age} años` : (b.min_age != null && b.min_age !== "" ? `${b.min_age}+ años` : ""));
                      return (
                        <li key={b.id} className="flex justify-between text-sm items-baseline gap-3">
                          <span className="text-slate-600">{b.label}{range && <span className="text-slate-400"> · {range}</span>}</span>
                          <span className="font-bold brand-navy whitespace-nowrap">{formatEur(Number(b.amount) || 0)}</span>
                        </li>
                      );
                    })}
                  </ul>
                  {a.age_brackets.some((b) => Object.values(b.associate_prices || {}).some((v) => v != null && v !== "")) && (
                    <p className="text-xs text-emerald-600 mt-3 font-semibold">Precios especiales para asociados disponibles en la inscripción.</p>
                  )}
                </>
              ) : (
                <ul className="space-y-2">
                  {a.tariffs?.map((t) => (
                    <li key={t.id} className="flex justify-between text-sm"><span className="text-slate-600">{t.label}</span><span className="font-bold brand-navy">{formatEur(t.amount)}</span></li>
                  ))}
                </ul>
              )}
              {a.discounts?.length > 0 && <p className="text-xs text-emerald-600 mt-3 font-semibold">Descuentos disponibles en la inscripción.</p>}
            </Section>
            {a.recommended_age && <Section icon={Baby} title="Edad recomendada"><p className="text-slate-600">{a.recommended_age}</p></Section>}
            <Section icon={MapPin} title="Punto de encuentro">
              <p className="text-slate-600">{a.meeting_point}</p>
              {a.maps_url && <a data-testid="maps-link" href={a.maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-[#003284] hover:text-[#FF7F27]"><ExternalLink size={15} /> Ver en Google Maps</a>}
            </Section>
            {a.cancellation && <Section icon={X} title="Condiciones de cancelación"><p className="text-slate-600 text-sm whitespace-pre-line">{a.cancellation}</p></Section>}
            <InquiryForm activityId={a.id} />
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white/90 backdrop-blur-xl border-t border-slate-200 p-4 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="hidden sm:block">
            <p className="text-xs text-slate-400">Desde</p>
            <p className="text-xl font-black brand-navy">{(() => {
              const from = activityFromPrice(a);
              return from != null ? formatEur(from) : "Consultar";
            })()}</p>
          </div>
          <button data-testid="detail-signup-btn" disabled={!canSignup} onClick={() => navigate(`/actividad/${a.id}/apuntarse`)} className="flex-1 sm:flex-none sm:px-16 bg-brand-orange text-white font-black text-lg rounded-full py-4 shadow-lg hover:bg-[#E56A15] disabled:opacity-40 transition-colors">
            {a.status === "cancelada" ? "Cancelada" : a.is_full ? "Completa" : a.inscriptions_closed ? "Inscripciones cerradas" : "Apuntarse"}
          </button>
        </div>
      </div>
    </div>
  );
}
